"use client";

import { useMemo, useState } from "react";
import { useFormStatus } from "react-dom";
import { useSearchParams } from "next/navigation";
import { Button, TextField } from "@mui/material";
import { GenericAutocomplete } from "./GenericAutocomplete";
import { ChannelType } from "@/interfaces/channelType";
import { County, State } from "@/postgres";

function SubmitButton() {
  const { pending } = useFormStatus();

  return (
    <Button
      className="w-xs"
      type="submit"
      variant="contained"
      disabled={pending}
      loading={pending}
    >
      Search
    </Button>
  );
}

interface SearchFormProps {
  channelTypes: Map<number, ChannelType>;
  counties: Map<string, County>;
  states: Map<string, State>;
}

export default function SearchForm({
  channelTypes,
  counties,
  states,
}: SearchFormProps) {
  const searchParams = useSearchParams();

  // Get current values from URL params
  const channelName = searchParams?.get("channel-name") || "";

  // Local state for form values (now using objects instead of IDs)
  const [selectedStates, setSelectedStates] = useState<string[]>(
    searchParams?.getAll("states") || [],
  );
  const [selectedCounties, setSelectedCounties] = useState<string[]>(
    searchParams?.getAll("counties") || [],
  );
  const [selectedChannelTypes, setSelectedChannelTypes] = useState<number[]>(
    searchParams?.getAll("channel-types").map((id) => parseInt(id)) || [],
  );

  // Filter counties based on selected states
  const filteredCounties = useMemo(() => {
    if (selectedStates.length === 0) return counties;
    const selectedStateIds = selectedStates.map((stateId) => stateId);
    return new Map(
      Array.from(counties.entries()).filter(([, county]) =>
        selectedStateIds.includes(county.state_id),
      ),
    );
  }, [counties, selectedStates]);

  return (
    <div className="flex flex-col gap-4">
      <TextField
        fullWidth
        label="Channel Name"
        name="channel-name"
        placeholder="Enter channel name..."
        defaultValue={channelName}
      />

      <div className="flex flex-row gap-4">
        <GenericAutocomplete
          label="State"
          options={Array.from(states.keys())}
          value={selectedStates}
          onChange={setSelectedStates}
          optionMap={states}
        />

        <GenericAutocomplete
          label="County"
          options={[...filteredCounties.keys()]}
          value={selectedCounties}
          onChange={setSelectedCounties}
          optionMap={counties}
        />

        <GenericAutocomplete<number>
          label="Channel Type"
          options={Array.from(channelTypes.keys())}
          value={selectedChannelTypes}
          onChange={setSelectedChannelTypes}
          optionMap={channelTypes}
        />

        <SubmitButton />
      </div>

      {selectedStates.map((stateId) => (
        <input
          key={`state-${stateId}`}
          type="hidden"
          name="states"
          value={stateId}
        />
      ))}
      {selectedCounties.map((countyId) => (
        <input
          key={`county-${countyId}`}
          type="hidden"
          name="counties"
          value={countyId}
        />
      ))}
      {selectedChannelTypes.map((channelTypeId) => (
        <input
          key={`channelType-${channelTypeId}`}
          type="hidden"
          name="channel-types"
          value={channelTypeId}
        />
      ))}
    </div>
  );
}
